import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTeamStore } from "../state/teamStore";
import { useRunStore } from "../state/runStore";
import { api } from "../utils/api";
import { formatLapTime, formatSetupName } from "../utils/formatters";
import TrackWeekSelector from "../components/TrackWeekSelector";
import type { RunSummary } from "../types";

interface DiffEntry {
  category: string;
  parameter: string;
  path: string;
  valueA: string;
  valueB: string;
  delta: number | null;
}

interface DiffResult {
  runA: { id: string; displayName: string | null; setupName: string };
  runB: { id: string; displayName: string | null; setupName: string };
  categories: Record<string, DiffEntry[]>;
  totalDiffs: number;
}

export default function SetupDiff() {
  const { trackWeekId: routeTrackWeekId } = useParams<{ trackWeekId: string }>();
  const { activeTeamId } = useTeamStore();
  const { runs, selectedTrackWeekId, fetchTrackWeeks, fetchRuns, setTrackWeek } = useRunStore();
  const [searchParams] = useSearchParams();
  const [runAId, setRunAId] = useState("");
  const [runBId, setRunBId] = useState("");
  const [result, setResult] = useState<DiffResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Deep-link from the Setup Library: ?run_a=&run_b=&track_week_id= preselects,
  // loads the deep-linked run's week (so it's pickable), and auto-runs if both ids present.
  useEffect(() => {
    const a = searchParams.get("run_a");
    const b = searchParams.get("run_b");
    const tw = searchParams.get("track_week_id");
    if (tw) setTrackWeek(tw);
    if (a) setRunAId(a);
    if (b) setRunBId(b);
    if (a && b) {
      let cancelled = false;
      setLoading(true);
      api<DiffResult>(`/api/comparison/setup-diff?run_a=${a}&run_b=${b}`)
        .then((data) => { if (!cancelled) { setResult(data); setCollapsed({}); } })
        .catch((err) => console.error("Setup diff failed:", err))
        .finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTeamId) fetchTrackWeeks(activeTeamId);
  }, [activeTeamId, fetchTrackWeeks]);

  useEffect(() => {
    if (activeTeamId) fetchRuns(activeTeamId, selectedTrackWeekId || undefined);
  }, [activeTeamId, selectedTrackWeekId, fetchRuns]);

  const handleCompare = async () => {
    if (!runAId || !runBId) return;
    setLoading(true);
    try {
      const data = await api<DiffResult>(`/api/comparison/setup-diff?run_a=${runAId}&run_b=${runBId}`);
      setResult(data);
      setCollapsed({});
    } catch (err) {
      console.error("Setup diff failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (cat: string) => {
    setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const runsByDriver = runs.reduce<Record<string, RunSummary[]>>((acc, r) => {
    const key = r.displayName || r.userId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  // A deep-linked run may belong to a week not currently loaded (cross-week
  // compare) — keep its option present so the selection stays visible + honored.
  const runIds = new Set(runs.map((r) => r.id));

  return (
    <div>
      {/* Tab content under the WeekWorkspace hero — no page title */}

        {!routeTrackWeekId && (
          <div style={{ marginBottom: "1.5rem" }}>
            <TrackWeekSelector />
          </div>
        )}

      {/* Run selectors */}
      <div className="card" style={{ marginBottom: "1.5rem", padding: "1.25rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "1rem", alignItems: "end" }}>
          <div>
            <label style={{ fontFamily: "var(--font-body)", fontSize: "0.85rem", fontWeight: 500, color: "var(--text-secondary)" }}>Setup A</label>
            <select className="select" value={runAId} onChange={(e) => setRunAId(e.target.value)} style={{ width: "100%", marginTop: "0.25rem" }}>
              <option value="">Select...</option>
              {runAId && !runIds.has(runAId) && <option value={runAId}>Preselected setup (other week)</option>}
              {Object.entries(runsByDriver).map(([driver, driverRuns]) => (
                <optgroup key={driver} label={driver}>
                  {driverRuns.map((r) => (
                    <option key={r.id} value={r.id}>{r.setupName} — {formatLapTime(r.bestLapTime)}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={handleCompare} disabled={!runAId || !runBId || loading}>
            {loading ? "Comparing…" : "Compare"}
          </button>
          <div>
            <label style={{ fontFamily: "var(--font-body)", fontSize: "0.85rem", fontWeight: 500, color: "var(--text-secondary)" }}>Setup B</label>
            <select className="select" value={runBId} onChange={(e) => setRunBId(e.target.value)} style={{ width: "100%", marginTop: "0.25rem" }}>
              <option value="">Select...</option>
              {runBId && !runIds.has(runBId) && <option value={runBId}>Preselected setup (other week)</option>}
              {Object.entries(runsByDriver).map(([driver, driverRuns]) => (
                <optgroup key={driver} label={driver}>
                  {driverRuns.map((r) => (
                    <option key={r.id} value={r.id}>{r.setupName} — {formatLapTime(r.bestLapTime)}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div>
          <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
              {result.totalDiffs} parameter{result.totalDiffs !== 1 ? "s" : ""} differ
            </span>
            <div style={{ display: "flex", gap: "1rem", fontFamily: "var(--font-body)", fontSize: "0.8125rem" }}>
              <span><strong style={{ color: "var(--live)" }}>A:</strong> {result.runA.displayName} / {formatSetupName(result.runA.setupName)}</span>
              <span><strong style={{ color: "var(--live)" }}>B:</strong> {result.runB.displayName} / {formatSetupName(result.runB.setupName)}</span>
            </div>
          </div>

          {result.totalDiffs === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: "2rem" }}>
              <span style={{ fontFamily: "var(--font-display)", fontSize: "1rem", fontWeight: 700, color: "var(--live)" }}>Setups are identical</span>
            </div>
          ) : (
            Object.entries(result.categories).map(([category, params]) => (
              <div key={category} className="card" style={{ marginBottom: "0.75rem", padding: 0, overflow: "hidden" }}>
                {/* Category header */}
                <button
                  onClick={() => toggleCategory(category)}
                  style={{
                    width: "100%", border: "none", background: "var(--live-soft)",
                    padding: "0.7rem 1rem", cursor: "pointer", display: "flex",
                    justifyContent: "space-between", alignItems: "center",
                    fontFamily: "var(--font-display)", fontSize: "0.95rem", fontWeight: 700,
                    letterSpacing: "-0.01em",
                    color: "var(--live-strong)",
                  }}
                >
                  <span>{category}</span>
                  <span style={{ fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums", color: "var(--text-secondary)" }}>
                    {params.length} diff{params.length !== 1 ? "s" : ""} {collapsed[category] ? "+" : "-"}
                  </span>
                </button>

                {!collapsed[category] && (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Parameter</th>
                        <th style={{ ...thStyle, color: "var(--live)" }}>Setup A</th>
                        <th style={{ ...thStyle, color: "var(--live)" }}>Setup B</th>
                        <th style={thStyle}>Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {params.map((p) => (
                        <tr key={p.path} style={{ borderBottom: "1px solid var(--rule-faint)" }}>
                          <td style={tdStyle}>{p.parameter}</td>
                          <td style={tdMonoStyle}>{p.valueA}</td>
                          <td style={tdMonoStyle}>{p.valueB}</td>
                          <td style={{
                            ...tdMonoStyle,
                            color: p.delta === null ? "var(--text-muted)" : p.delta > 0 ? "var(--flag-orange)" : "var(--live)",
                          }}>
                            {p.delta !== null ? `${p.delta > 0 ? "+" : ""}${p.delta}` : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: "0.55rem 1rem", textAlign: "left", fontSize: "0.8125rem",
  fontFamily: "var(--font-body)", fontWeight: 500,
  color: "var(--text-secondary)",
  borderBottom: "1px solid var(--rule)",
};

const tdStyle: React.CSSProperties = {
  padding: "0.4rem 1rem", fontSize: "0.8125rem",
  fontFamily: "var(--font-body)",
};

const tdMonoStyle: React.CSSProperties = {
  padding: "0.4rem 1rem", fontSize: "0.8125rem",
  fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums",
};
